import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { PropertyConfig } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { validatorEntry } from "@cervval.js/common/src/services/validation.impl/validators/validator";
import { formatMinutesHHMM, formatSecondsHHMM, minutesToSeconds, parseHumanTimeString } from "@cervval.js/common/src/tools/time.tools";
import { CjsDurationPicker, DurationPickerOptions } from "../../../pickers/durationPicker/durationPicker";
import { EditorWithPicker, EditorWithPickerOptions, defaultEditorWithPickerOptions } from "../editorWithPicker";
import { DurationInRangesValidator } from "./durationEditor.validators";

export interface DurationEditorOptions extends EditorWithPickerOptions {

    /**
     * Disable "time" type for input, use default TimeEditor regex validators
     * ex: 1402 => 14h02
     */
    enableTimeInputType?: boolean;
}


export const defaultDurationEditorOptions: DurationEditorOptions = {
    ...defaultEditorWithPickerOptions,
    buttonIcon: { name: "fa-hourglass-clock" }
};

@CustomElement({
    name: "cjs-duration-editor",
    elementCssClasses: ["cjs-editor"]
})
export class CjsDurationEditor extends EditorWithPicker<number> {

    /** Attributes options of editor */
    @TypedWebComponentOptions(defaultDurationEditorOptions)
    public readonly options!: WebComponentOptions<DurationEditorOptions>;

    /** Shortcut to duration picker options */
    public get pickerOptions(): WebComponentOptions<DurationPickerOptions> {
        return this._picker.options;
    }

    /**
     * The DatePicker used to edit the value of the input
     */
    protected _picker = new CjsDurationPicker();

    constructor(options?: Partial<DurationEditorOptions>, datePickerOptions?: Partial<DurationPickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
        this.pickerOptions.setWithoutRefresh(datePickerOptions);
    }

    /** When component refresh */
    public override refresh(): void {
        super.refresh();
        // Force input type to time if picker don't use days
        this.getInput().inputOptions.setAndRefreshIfChanged({ inputType: this.options.enableTimeInputType && !this.pickerOptions.useDays ? "time" : "text" });
    }

    /**
     * Instantiate picker on first call, and refresh picker based on value and options (called before showPopup)
     */
    protected _refreshPicker(): CjsDurationPicker {
        if (this._value != null) {
            let value = this._value;
            if (this._value < 0 && !this.pickerOptions.allowNegative) {
                value = 0;
            }
            this._picker.setValue(value);
            this._picker.refresh();
        } else {// Undefined, reset picker // TODO Create and replace with empty picker
            this._picker = new CjsDurationPicker(this.pickerOptions);
        }

        return this._picker;
    }

    /**
     * Format the current durationInMinutes to a string which can be display for Humans
     */
    public convertValueToInputValue(duration: number | undefined): string {
        if (typeof duration === "undefined") {
            return "";
        }
        return this.pickerOptions.useSeconds ? formatSecondsHHMM(duration) : formatMinutesHHMM(duration);
    }

    /**
     * Format the displayed value in the input and return durationInMinutes from the formatted string
     */
    protected _convertInputValueToValue(inputValue: string | undefined): number | undefined {
        if (inputValue == null || inputValue === "") {
            return undefined;
        }
        const value = parseHumanTimeString(inputValue, { acceptNegative: true, acceptAboveH24: true });
        return value;
    }

    /**
     * Gets the value corresponding to the current input value
     */
    protected _getValueFromInputComponent(): number | undefined {
        // Get value (in minutes)
        const value = this._convertInputValueToValue(this.getInput().components.inputText.value);
        if (!value) { return value; } // If value is 0, no need to check if useSeconds mode or not, we return 0 right away
        return this.pickerOptions.useSeconds ? minutesToSeconds(value) : value;
    }

    /**
     * Decorate the input depending on input value validity
     * @returns false if value is invalid
     */
    protected override _validateInputValue(value: number | undefined): boolean {
        if (!super._validateInputValue(value)) {
            return false;
        }
        return this._validateRanges(value);
    }

    /** Validate value in enabled ranges of durationEditor */
    protected _validateRanges(value?: number): boolean {
        const rangesPropertyConfig: PropertyConfig = {
            propertyName: "ranges",
            label: "",
            typeUid: PropertyConfigType.NUMBER,
            validators: {
                ...validatorEntry(DurationInRangesValidator, { ranges: this.pickerOptions.ranges ?? [], useSeconds: this.pickerOptions.useSeconds })
            }
        };
        const propsValues: Record<string, unknown> = {
            "ranges": value ?? this._value
        };
        const validatedProps: Record<string, PropertyConfig> = {
            "ranges": rangesPropertyConfig
        };

        const errors = this._validation.validate(validatedProps, propsValues);

        this.setValidationError(errors["ranges"]);

        // Check if no error given === valid duration
        return Object.keys(errors).length === 0;
    }
}